import { createSlice, PayloadAction, createSelector } from '@reduxjs/toolkit'
import { CartItem, ComboSelections } from '@/types/cart'
import { MenuItem } from '@/types/menu'
import { SelectedModifier } from '@/types/modifier'
import { rowHasDynamicPricing, calculateRowTotal } from '@/utils/ingredientPricing'

interface CartState {
  items: CartItem[]
  isOpen: boolean
  subtotal: number
  total: number
}

const initialState: CartState = {
  items: [],
  isOpen: false,
  subtotal: 0,
  total: 0,
}

function loadPersistedCart(): CartState {
  try {
    const stored = localStorage.getItem('restohub-cart')
    if (!stored) return initialState

    const parsed = JSON.parse(stored)
    if (typeof parsed !== 'object' || parsed === null) return initialState

    return {
      ...initialState,
      items: Array.isArray(parsed.items) ? parsed.items : [],
      subtotal: typeof parsed.subtotal === 'number' && isFinite(parsed.subtotal) ? parsed.subtotal : 0,
      total: typeof parsed.total === 'number' && isFinite(parsed.total) ? parsed.total : 0,
    }
  } catch {
    try { localStorage.removeItem('restohub-cart') } catch { /* ignore */ }
    return initialState
  }
}

function persistCart(state: CartState) {
  try {
    localStorage.setItem('restohub-cart', JSON.stringify({
      items: state.items,
      subtotal: state.subtotal,
      total: state.total,
    }))
  } catch { /* ignore */ }
}

// Helper to compute modifier/main-meal total with dynamic pricing support
function computeModifierTotal(
  menuItem: MenuItem,
  modifiers: SelectedModifier[],
  skipGroupId?: string
): number {
  const byRow = new Map<string, string[]>()
  for (const mod of modifiers) {
    if (skipGroupId && mod.groupId === skipGroupId) continue
    const list = byRow.get(mod.groupId) || []
    const qty = mod.quantity ?? 1
    for (let i = 0; i < qty; i++) list.push(mod.optionId)
    byRow.set(mod.groupId, list)
  }
  let total = 0
  for (const [rowId, optionIds] of byRow) {
    if (rowHasDynamicPricing(menuItem, rowId)) {
      total += calculateRowTotal(menuItem, rowId, optionIds)
    } else {
      for (const mod of modifiers) {
        if (mod.groupId === rowId) {
          const adj = isNaN(mod.priceAdjustment) ? 0 : mod.priceAdjustment
          total += adj
        }
      }
    }
  }
  return total
}

// Helper function to calculate item price (including combo selections)
function calculateItemPrice(
  menuItem: MenuItem,
  modifiers: SelectedModifier[],
  quantity: number,
  comboSelections?: ComboSelections
): number {
  const basePrice = isNaN(menuItem.price) || menuItem.price === null || menuItem.price === undefined
    ? 0 : menuItem.price

  // Skip ingredient variation group from modifier total
  const skipGroupId = menuItem.ingredientVariationGroup?.id
  const modifierTotal = computeModifierTotal(menuItem, modifiers, skipGroupId)

  // Add combo group selection prices
  let comboGroupTotal = 0
  if (comboSelections?.groupSelections) {
    comboGroupTotal = comboSelections.groupSelections.reduce(
      (sum, sel) => {
        const price = isNaN(sel.additionalPrice) ? 0 : sel.additionalPrice
        return sum + price
      },
      0
    )
  }

  // Add main meal customization prices (with dynamic pricing)
  let mainMealTotal = 0
  if (comboSelections?.mainMealCustomizations) {
    mainMealTotal = computeModifierTotal(menuItem, comboSelections.mainMealCustomizations)
  }

  // Add ingredient variation prices (e.g. size upgrades in combo rows)
  let variationsTotal = 0
  if (comboSelections?.ingredientVariationSelections) {
    variationsTotal = comboSelections.ingredientVariationSelections.reduce(
      (sum, vs) => {
        const adj = isNaN(vs.priceAdjustment) ? 0 : vs.priceAdjustment
        return sum + adj
      },
      0
    )
  }

  return Math.round((basePrice + modifierTotal + comboGroupTotal + mainMealTotal + variationsTotal) * quantity * 100) / 100
}

// Helper function to calculate totals
function calculateTotals(state: CartState) {
  state.subtotal = Math.round(state.items.reduce((sum, item) => sum + item.itemTotal, 0) * 100) / 100
  state.total = state.subtotal
}

export const cartSlice = createSlice({
  name: 'cart',
  initialState: loadPersistedCart(),
  reducers: {
    addItem: (
      state,
      action: PayloadAction<{
        menuItem: MenuItem
        selectedModifiers: SelectedModifier[]
        quantity: number
        specialInstructions?: string
        comboSelections?: ComboSelections
      }>
    ) => {
      const { menuItem, selectedModifiers, quantity, specialInstructions, comboSelections } =
        action.payload

      const itemTotal = calculateItemPrice(
        menuItem,
        selectedModifiers,
        quantity,
        comboSelections
      )

      const cartItem: CartItem = {
        id: `${menuItem.id}-${Date.now()}`, // Generate unique ID
        menuItem,
        selectedModifiers,
        quantity,
        specialInstructions,
        itemTotal,
        comboSelections,
      }

      state.items.push(cartItem)
      calculateTotals(state)
      persistCart(state)
    },

    removeItem: (state, action: PayloadAction<string>) => {
      state.items = state.items.filter((item) => item.id !== action.payload)
      calculateTotals(state)
      persistCart(state)
    },

    updateQuantity: (
      state,
      action: PayloadAction<{ id: string; quantity: number }>
    ) => {
      const item = state.items.find((item) => item.id === action.payload.id)
      if (item) {
        item.quantity = action.payload.quantity
        item.itemTotal = calculateItemPrice(
          item.menuItem,
          item.selectedModifiers,
          item.quantity,
          item.comboSelections
        )
        calculateTotals(state)
        persistCart(state)
      }
    },

    updateItem: (
      state,
      action: PayloadAction<{
        id: string
        selectedModifiers: SelectedModifier[]
        quantity: number
        specialInstructions: string
      }>
    ) => {
      const item = state.items.find((item) => item.id === action.payload.id)
      if (item) {
        item.selectedModifiers = action.payload.selectedModifiers
        item.quantity = action.payload.quantity
        item.specialInstructions = action.payload.specialInstructions
        item.itemTotal = calculateItemPrice(
          item.menuItem,
          item.selectedModifiers,
          item.quantity,
          item.comboSelections
        )
        calculateTotals(state)
        persistCart(state)
      }
    },

    clearCart: (state) => {
      state.items = []
      calculateTotals(state)
      persistCart(state)
    },

    toggleCart: (state) => {
      state.isOpen = !state.isOpen
    },

    openCart: (state) => {
      state.isOpen = true
    },

    closeCart: (state) => {
      state.isOpen = false
    },
  },
})

export const {
  addItem,
  removeItem,
  updateQuantity,
  updateItem,
  clearCart,
  toggleCart,
  openCart,
  closeCart,
} = cartSlice.actions

// Selectors
export const selectCartItems = (state: { cart: CartState }) => state.cart.items
export const selectIsCartOpen = (state: { cart: CartState }) => state.cart.isOpen

// Memoized selector to avoid unnecessary re-renders
const selectCart = (state: { cart: CartState }) => state.cart
export const selectCartTotals = createSelector(
  [selectCart],
  (cart) => ({
    subtotal: cart.subtotal,
    total: cart.total,
  })
)

export default cartSlice.reducer
